import React, { useState, useMemo, useEffect } from 'react';
import {
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { detectIssues, validateDate, validateDescription, validateAmount } from '../../utils/transactionValidator';
import { useLocale } from '../../contexts/LocaleContext';
import { convertDateFormat } from '../../utils/dateFormatter';

const TransactionTable = ({ transactions, onTransactionsChange, readonly = false, showIssuesOnly = false, statementYear }) => {
  const { dateFormat } = useLocale();
  const [editingRow, setEditingRow] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 50;

  // Filter transactions if showIssuesOnly is true
  const filteredTransactions = useMemo(() => {
    if (!showIssuesOnly) return transactions;

    return transactions.filter(trans => {
      const issues = detectIssues(trans);
      return issues.hasAnyIssue;
    });
  }, [transactions, showIssuesOnly]);

  // Calculate pagination on filtered transactions
  const totalPages = Math.ceil(filteredTransactions.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentTransactions = filteredTransactions.slice(startIndex, endIndex);

  // Calculate quality stats
  const qualityStats = useMemo(() => {
    let unknownDates = 0;
    let unknownDescriptions = 0;
    let unknownAmounts = 0;

    transactions.forEach(trans => {
      const issues = detectIssues(trans);
      if (issues.hasUnknownDate) unknownDates++;
      if (issues.hasUnknownDescription) unknownDescriptions++;
      if (issues.hasUnknownAmount) unknownAmounts++;
    });

    return { unknownDates, unknownDescriptions, unknownAmounts };
  }, [transactions]);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [showIssuesOnly]);

  // Start editing a row
  const handleStartEdit = (index) => {
    const filteredIndex = startIndex + index;
    const transaction = filteredTransactions[filteredIndex];
    const actualIndex = transactions.indexOf(transaction);
    setEditingRow(actualIndex);
    setEditValues(transactions[actualIndex]);
  };

  // Save edited row
  const handleSaveEdit = () => {
    if (editingRow === null) return;

    // Validate before saving
    const dateValid = validateDate(editValues.date);
    const descValid = validateDescription(editValues.description);
    const amountValid = validateAmount(editValues.amount);

    if (!dateValid.isValid && !dateValid.isUnknown) {
      alert('Invalid date format. Please use DD/MM/YYYY or DD/MM/YY');
      return;
    }

    if (!descValid.isValid && !descValid.isUnknown) {
      alert('Invalid description. ' + descValid.error);
      return;
    }

    if (!amountValid.isValid && !amountValid.isUnknown) {
      alert('Invalid amount. ' + amountValid.error);
      return;
    }

    // Update transactions
    const updatedTransactions = [...transactions];
    updatedTransactions[editingRow] = { ...editValues };
    onTransactionsChange(updatedTransactions);

    setEditingRow(null);
    setEditValues({});
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingRow(null);
    setEditValues({});
  };

  // Delete a row
  const handleDelete = (index) => {
    const filteredIndex = startIndex + index;
    const transactionToDelete = filteredTransactions[filteredIndex];
    const actualIndex = transactions.indexOf(transactionToDelete);
    if (window.confirm('Are you sure you want to delete this transaction?')) {
      const updatedTransactions = transactions.filter((_, i) => i !== actualIndex);
      onTransactionsChange(updatedTransactions);

      // Adjust page if needed
      if (currentTransactions.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    }
  };

  // Add a new row
  const handleAddRow = () => {
    const newTransaction = {
      date: '',
      description: '',
      amount: '0.00'
    };

    const updatedTransactions = [...transactions, newTransaction];
    onTransactionsChange(updatedTransactions);

    // Go to last page
    const newTotalPages = Math.ceil(updatedTransactions.length / rowsPerPage);
    setCurrentPage(newTotalPages);
  };

  // Update edit value
  const handleEditChange = (field, value) => {
    setEditValues({
      ...editValues,
      [field]: value
    });
  };

  // Render a cell (editable or readonly)
  const renderCell = (transaction, field, rowIndex, actualIndex) => {
    const isEditing = editingRow === actualIndex;

    if (isEditing) {
      // Edit mode
      return (
        <input
          type="text"
          value={editValues[field]}
          onChange={(e) => handleEditChange(field, e.target.value)}
          className="w-full px-2 py-1 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          autoFocus={field === 'date'}
        />
      );
    } else {
      // Display mode
      let value = transaction[field];
      const issues = detectIssues(transaction);
      let isIssue = false;

      if (field === 'date' && (issues.hasUnknownDate || issues.hasInvalidDate)) isIssue = true;
      if (field === 'description' && (issues.hasUnknownDescription || issues.hasInvalidDescription)) isIssue = true;
      if (field === 'amount' && (issues.hasUnknownAmount || issues.hasInvalidAmount)) isIssue = true;

      // Format date according to user's preference
      if (field === 'date' && value && value !== 'Unknown Date') {
        value = convertDateFormat(value, dateFormat);
      }

      return (
        <div className={`flex items-center ${isIssue ? 'text-amber-800' : ''}`}>
          {isIssue && <ExclamationTriangleIcon className="h-4 w-4 text-amber-600 mr-1 flex-shrink-0" />}
          <span className="break-words">{value || 'N/A'}</span>
        </div>
      );
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Filter Status Indicator */}
      {showIssuesOnly && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 mb-3">
          <div className="flex items-center justify-center">
            <span className="text-sm font-medium text-blue-900">
              🔍 Showing only transactions with issues ({filteredTransactions.length} of {transactions.length})
            </span>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg">
        <table className="w-full table-fixed divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-32">
                Date
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                Description
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-32">
                Amount
              </th>
              {!readonly && (
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider w-24">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {currentTransactions.map((transaction, index) => {
              const actualIndex = transactions.indexOf(transaction);
              const issues = detectIssues(transaction);
              const hasIssue = issues.hasAnyIssue;
              const isEditing = editingRow === actualIndex;

              return (
                <tr
                  key={actualIndex}
                  className={`
                    ${hasIssue && !isEditing ? 'bg-amber-50' : ''}
                    ${isEditing ? 'bg-blue-50' : 'hover:bg-gray-50'}
                  `}
                >
                  <td className="px-4 py-3 text-sm">
                    {renderCell(transaction, 'date', index, actualIndex)}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {renderCell(transaction, 'description', index, actualIndex)}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {renderCell(transaction, 'amount', index, actualIndex)}
                  </td>
                  {!readonly && (
                    <td className="px-4 py-3 text-sm text-center">
                      {isEditing ? (
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            onClick={handleSaveEdit}
                            className="text-green-600 hover:text-green-800"
                            title="Save"
                          >
                            <CheckIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="text-gray-600 hover:text-gray-800"
                            title="Cancel"
                          >
                            <XMarkIcon className="h-5 w-5" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            onClick={() => handleStartEdit(index)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Edit"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(index)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer with pagination and add button */}
      <div className="flex items-center justify-between mt-4 px-2">
        <div className="text-sm text-gray-600">
          {showIssuesOnly ? (
            <>
              Showing {startIndex + 1}-{Math.min(endIndex, filteredTransactions.length)} of {filteredTransactions.length} transactions with issues
              <span className="text-gray-400"> (filtered from {transactions.length} total)</span>
            </>
          ) : (
            <>Showing {startIndex + 1}-{Math.min(endIndex, transactions.length)} of {transactions.length} transactions</>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}

        {/* Add Row Button */}
        {!readonly && (
          <button
            onClick={handleAddRow}
            className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 border border-blue-300 rounded hover:bg-blue-50"
          >
            + Add Row
          </button>
        )}
      </div>
    </div>
  );
};

export default TransactionTable;
